// eslint-disable-next-line @altermaind/you-shall-not-pass
import { ComponentProps, FC, useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Card, Typography, useThemeValue } from '@altermaind/web/ui';
import { SelectItem, Form } from '@/_shared/components/Form';
import { convertSizeToNumber } from '@/_shared/helpers';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import {
  getDepartmentsCall,
  getDepartmentTeamsCall,
  ResponseGetDepartmentTeams,
} from '@/departments/state/api';
import { Department } from '@/departments/state/types';
import { Employer, getEmployerByIdCall, putEmployerByIdCall } from '@/people/state';
import { AccountStatus } from '@/swagger/constants/anagraphics-enums';
import { formatDateToPresent } from '../../helpers/convertDateAndTimeToFormatForBackend';
import { useNotification } from '../../hooks';
import { AlertBadge } from '../AlertBadge';
import { Tooltip } from '../Tooltip';

type Team = NonNullable<ResponseGetDepartmentTeams['data']>[number];

type Props = {
  employer: Employer;
  setEdit: (value: boolean) => void;
};

export const EditProfile: FC<Props> = ({ employer, setEdit }) => {
  const t = useTranslations('profileCard.editProfile');
  const tShared = useTranslations();
  const { notify } = useNotification();
  const { genericResponsiveValue } = useResponsiveStyle();

  const [isDeptText, setIsDeptText] = useState(false);
  const [teamsList, setTeamsList] = useState<Team[]>([]);

  const {
    execute: executeGetDepartments,
    data: departments,
    loading: loadingGetDepartments,
  } = getDepartmentsCall.hook();
  const { execute: executeGetDepartmentTeams, loading: loadingGetDepartmentTeams } =
    getDepartmentTeamsCall.hook();

  const isDeptUnknown = employer.department?.name?.toLowerCase() === 'unknown';
  const isAreaUnknown = employer.area?.name?.toLowerCase() === 'unknown';

  const defaultDepartment = (() => {
    if (employer.department?.id && !isDeptUnknown) {
      return { value: employer.department.id, label: employer.department.name } as SelectItem;
    }
    if (!isDeptUnknown) return employer?.department?.name || '';
    return '';
  })();

  const defaultArea = (() => {
    if (employer.area?.id && !isAreaUnknown) {
      return { value: employer.area.id, label: employer.area.name } as SelectItem;
    }
    if (!isAreaUnknown) return employer?.area?.name || '';
    return '';
  })();

  const {
    handleSubmit,
    formState: { isValid },
    control,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      firstName: employer?.firstName || '',
      lastName: employer?.lastName || '',
      email: employer?.email || '',
      fiscalCode: employer?.fiscalCode || '',
      role: employer?.role || '',
      department: defaultDepartment,
      area: defaultArea,
      emailManager: employer?.manager?.email || '',
      jobLevel: employer?.jobLevel || '',
      birthDate: employer?.birthDate ? formatDateToPresent(employer.birthDate) : '',
    },
  });

  const fieldValues = watch();
  const deptValue = watch('department');

  const fetchTeams = useCallback(
    (deptId: string) => {
      executeGetDepartmentTeams({
        departmentId: deptId,
        successSaga: ({ response }) => {
          setTeamsList(response?.data || []);
        },
      });
    },
    [executeGetDepartmentTeams],
  );

  const { execute: executePutEmployerById, loading: loadingPutEmployerById } =
    putEmployerByIdCall.hook();
  const { execute: executeGetEmployerById } = getEmployerByIdCall.hook();

  const deptItems: SelectItem[] = useMemo(
    () =>
      ((departments as Department[] | undefined) || [])
        .filter(d => d.name?.toLowerCase() !== 'unknown')
        .map(d => ({
          value: d.id || '',
          label: d.name || '',
        })),
    [departments],
  );

  const teamItems: SelectItem[] = useMemo(
    () =>
      teamsList
        .filter(team => team.name?.toLowerCase() !== 'unknown')
        .map(team => ({ value: team.id || '', label: team.name || '' })),
    [teamsList],
  );

  const isDeptSelected =
    !isDeptText && deptValue && typeof deptValue === 'object' && 'value' in deptValue;

  const handleDeptModeChange = useCallback(
    (newMode: 'dropdown' | 'text') => {
      setIsDeptText(newMode === 'text');
      setValue('department', '');
      setValue('area', '');
      setTeamsList([]);
    },
    [setValue],
  );

  const handleTeamModeChange = useCallback(() => {
    setValue('area', '');
  }, [setValue]);

  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    (data: FieldValues) => {
      if (isValid) {
        const birthDateRaw = data.birthDate
          ? dayjs(data.birthDate, 'DD/MM/YYYY').toISOString()
          : '';
        const formatted = birthDateRaw ? dayjs(birthDateRaw).format('YYYY-MM-DD') : '';
        const departmentName =
          typeof data.department === 'object'
            ? (data.department as SelectItem).label
            : data.department;
        const teamName =
          typeof data.area === 'object' ? (data.area as SelectItem).label : data.area;

        executePutEmployerById({
          employeeId: employer.id,
          firstName: data.firstName,
          lastName: data.lastName,
          birthDate: formatted,
          fiscalCode: data.fiscalCode,
          email: data.email,
          role: data.role,
          department: departmentName,
          area: teamName,
          emailManager: data.emailManager,
          jobLevel: data.jobLevel,
          successSaga: () => {
            setEdit(false);
            notify(tShared('success.genericApiSuccess'), 'success');
            executeGetEmployerById({ employeeId: employer.id });
          },
          errorSaga: response => {
            const errorMap = {
              duplicateEmployee: '/problems/duplicate-employee',
              managerError: '/problems/manager-assignment',
            };
            if (response.error.type === errorMap.duplicateEmployee) {
              return notify(t('errors.emailAlreadyInUse'), 'error');
            }
            if (response.error.type === errorMap.managerError) {
              return notify(t('errors.managerError'), 'error');
            }
            return notify(tShared('errors.genericApiError'), 'error');
          },
        });
      }
    },
    [
      employer.id,
      executeGetEmployerById,
      executePutEmployerById,
      isValid,
      notify,
      setEdit,
      t,
      tShared,
    ],
  );

  const gapXS = convertSizeToNumber(`${useThemeValue('sizes', 'xs')}`);
  const rowDirection = genericResponsiveValue<ComponentProps<typeof Box>['flexDirection']>({
    breakPoints: { desktop: 'row' },
    defaultValue: 'column',
  });

  const handleDeptSelect = useCallback(
    (item: SelectItem) => {
      const dept = (departments as Department[] | undefined)?.find(d => d.id === item.value);
      setValue('emailManager', dept?.manager?.email || '');
      setValue('area', '');
      setTeamsList([]);
      fetchTeams(item.value);
    },
    [departments, fetchTeams, setValue],
  );

  const handleTeamSelect = useCallback(
    (item: SelectItem) => {
      const selectedTeam = teamsList.find(team => team.id === item.value);
      setValue('emailManager', selectedTeam?.manager?.email || '');
    },
    [teamsList, setValue],
  );

  useEffect(() => {
    executeGetDepartments({});
    if (employer.department?.id) {
      fetchTeams(employer.department.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Card mt='s' pb='xxxl' overflow='visible'>
        <Typography variant='h2'>{t('title')}</Typography>

        {/* Row 1: First name, Last name, Email */}
        <Box display='flex' mt='s' flexDirection={rowDirection} style={{ gap: gapXS }}>
          <Box flex='1'>
            <Form.FormInput
              // @ts-ignore
              control={control}
              name='firstName'
              label={t('fields.firstName')}
              placeholder={t('fields.firstName')}
              additonalProps={{ required: true }}
              hasError={
                fieldValues.firstName === '' ||
                employer.issues.find(issue => issue.fieldName === 'firstName') !== undefined
              }
              labelColor='grey/light'
            />
          </Box>
          <Box flex='1'>
            <Form.FormInput
              // @ts-ignore
              control={control}
              name='lastName'
              label={t('fields.lastName')}
              placeholder={t('fields.lastName')}
              additonalProps={{ required: true }}
              hasError={
                fieldValues.lastName === '' ||
                employer.issues.find(issue => issue.fieldName === 'lastName') !== undefined
              }
              labelColor='grey/light'
            />
          </Box>
          <Box flex='1'>
            {employer.status !== AccountStatus.ENABLED ? (
              <Form.FormInput
                // @ts-ignore
                control={control}
                name='email'
                label={t('fields.email')}
                placeholder={t('fields.email')}
                additonalProps={{ required: true }}
                hasError={
                  fieldValues.email === '' ||
                  !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(fieldValues.email) ||
                  employer.issues.find(issue => issue.fieldName === 'email') !== undefined
                }
                labelColor='grey/light'
              />
            ) : (
              <>
                <Box mb='xxs' display='flex' alignItems='center'>
                  <Typography variant='p2/light' fontWeight='400' mt='none'>
                    {t('fields.email')}
                  </Typography>
                  <Tooltip
                    iconProps={{ size: 'xs', fill: 'grey/light' }}
                    containerProps={{ ml: 'xxs' }}
                  >
                    {t('fields.emailTooltip')}
                  </Tooltip>
                </Box>
                <Form.FormInput
                  // @ts-ignore
                  control={control}
                  name='email'
                  placeholder={t('fields.email')}
                  hasError={
                    fieldValues.email === '' ||
                    !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(fieldValues.email) ||
                    employer.issues.find(issue => issue.fieldName === 'email') !== undefined
                  }
                  disabled={employer.status === AccountStatus.ENABLED}
                  labelColor='grey/light'
                />
              </>
            )}
          </Box>
        </Box>

        {/* Profile details */}
        <Typography mt='s' variant='h4'>
          {t('details')}
        </Typography>

        {/* Role information */}
        <Typography mt='xs' variant='p1/bold'>
          {t('roleInfo')}
        </Typography>

        {/* Row 2: Role, Job level */}
        <Box display='flex' mt='xs' flexDirection={rowDirection} style={{ gap: gapXS }}>
          <Box flex='1'>
            <Form.FormInput
              // @ts-ignore
              control={control}
              name='role'
              label={t('fields.role')}
              placeholder={t('fields.role')}
              hasError={employer.issues.find(issue => issue.fieldName === 'role') !== undefined}
              labelColor='grey/light'
            />
          </Box>
          <Box flex='1'>
            <Form.FormInput
              // @ts-ignore
              control={control}
              name='jobLevel'
              label={t('fields.jobLevel')}
              placeholder={t('fields.jobLevel')}
              hasError={employer.issues.find(issue => issue.fieldName === 'jobLevel') !== undefined}
              labelColor='grey/light'
            />
          </Box>
          <Box flex='1' />
        </Box>

        {/* Row 3: Department, Team, Manager */}
        <Box display='flex' mt='xs' flexDirection={rowDirection} style={{ gap: gapXS }}>
          {/* Department */}
          <Box flex='1'>
            <Form.FormSelectOrCreate
              // @ts-ignore
              control={control}
              name='department'
              label={t('fields.department')}
              createLabel={t('fields.createDepartment')}
              selectLabel={t('fields.chooseDepartment')}
              items={deptItems}
              placeholder={t('fields.departmentPlaceholder')}
              loading={loadingGetDepartments}
              hasError={
                employer.issues.find(issue => issue.fieldName === 'department') !== undefined
              }
              backgroundColorHeader='grey/light'
              onItemSelect={handleDeptSelect}
              onModeChange={handleDeptModeChange}
            />
          </Box>

          {/* Team */}
          <Box flex='1'>
            <Box
              style={{
                opacity: !isDeptText && !isDeptSelected ? 0.5 : 1,
                pointerEvents: !isDeptText && !isDeptSelected ? 'none' : 'auto',
              }}
            >
              <Form.FormSelectOrCreate
                // @ts-ignore
                control={control}
                name='area'
                label={t('fields.area')}
                createLabel={t('fields.createTeam')}
                selectLabel={t('fields.chooseTeam')}
                items={teamItems}
                placeholder={t('fields.teamPlaceholder')}
                loading={loadingGetDepartmentTeams}
                hasError={employer.issues.find(issue => issue.fieldName === 'area') !== undefined}
                backgroundColorHeader='grey/light'
                onItemSelect={handleTeamSelect}
                onModeChange={handleTeamModeChange}
                forcedMode={isDeptText ? 'text' : undefined}
                showToggle={!!isDeptSelected}
              />
            </Box>
          </Box>

          {/* Manager */}
          <Box flex='1'>
            <Form.FormInput
              // @ts-ignore
              control={control}
              name='emailManager'
              label={t('fields.manager')}
              placeholder={t('fields.manager')}
              hasError={
                (fieldValues.emailManager !== '' &&
                  !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(fieldValues.emailManager)) ||
                employer.issues.find(issue => issue.fieldName === 'manager') !== undefined
              }
              labelColor='grey/light'
            />
          </Box>
        </Box>

        {/* Personal details */}
        <Typography mt='s' variant='p1/bold'>
          {t('anagraphicDetails')}
        </Typography>

        {/* Row 4: Birth date, Fiscal code */}
        <Box display='flex' mt='xs' flexDirection={rowDirection} style={{ gap: gapXS }}>
          <Box flex='1'>
            <Typography
              variant='p2/light'
              fontWeight='400'
              mt='none'
              color='grey/light'
              lineHeight='xs'
              mb={12 as never} // Aligned spacing between label and input as in the other form inputs
            >
              {t('fields.birthDate')}
            </Typography>
            <Form.FormDate
              // @ts-ignore
              control={control}
              name='birthDate'
              placeholder={t('fields.birthDatePlaceholder')}
              minDate={dayjs().subtract(100, 'year').format('DD/MM/YYYY')}
            />
          </Box>
          <Box flex='1'>
            <Form.FormInput
              // @ts-ignore
              control={control}
              name='fiscalCode'
              label={t('fields.fiscalCode')}
              placeholder={t('fields.fiscalCode')}
              hasError={
                employer.issues.find(issue => issue.fieldName === 'fiscalCode') !== undefined
              }
              labelColor='grey/light'
            />
          </Box>
          <Box flex='1' />
        </Box>

        {employer?.manager === undefined && (
          <Box mt='s'>
            <AlertBadge type='white' iconName='lightBulb' label={t('alerts.emailManagerInfo')} />
          </Box>
        )}
        {employer.issues.find(issue => issue.fieldName === 'manager') !== undefined && (
          <Box mt='s'>
            <AlertBadge type='error' iconName='info' label={t('alerts.emailManagerWrong')} />
          </Box>
        )}
      </Card>
      <Box
        mt='xxxs'
        alignItems='center'
        display='flex'
        flexDirection='row'
        justifyContent='flex-end'
      >
        <Form.Buttons.Cta variant='link/primary' onClick={() => setEdit(false)} mr='s' mt='s'>
          {t('buttons.cancel')}
        </Form.Buttons.Cta>
        <Form.Buttons.Submit
          loading={loadingPutEmployerById}
          disabled={!isValid || loadingPutEmployerById}
          mt='s'
          data-testid='save-edit-employer'
        >
          {t('buttons.save')}
        </Form.Buttons.Submit>
      </Box>
    </form>
  );
};
